import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, CheckCircle2, Sheet, Undo2 } from 'lucide-react';
import { PageHeader, DataTable } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getCommissionById, completeCommission, reverseCommission } from '../../services/api/callCenterCommission';
import { exportCommissionDetailsExcel } from '../../utils/excelExport';
import { formatDateTime } from '../../utils/dateUtils';
import styles from './CommissionDetailsPage.module.css';

const CommissionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const canFinalize = hasPermission('finalize_callcenter_commission');
  const canReverse = hasPermission('reverse_callcenter_commission');

  const [commission, setCommission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isReversing, setIsReversing] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const loadCommission = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getCommissionById(id);
      setCommission(data.commission);
    } catch (err) {
      console.error('Error loading commission:', err);
      showError(err.response?.data?.message || t('callCenterCommission.failedToLoadCommission'));
    } finally {
      setIsLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCommission();
  }, [loadCommission]);

  const handleMarkCompleted = async () => {
    const ok = await confirm(t('callCenterCommission.completeConfirmMessage'), {
      confirmText: t('callCenterCommission.markCompleted'),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;

    setIsCompleting(true);
    try {
      await completeCommission(id);
      success(t('callCenterCommission.completeSuccess'));
      loadCommission();
    } catch (err) {
      console.error('Error completing commission:', err);
      showError(err.response?.data?.message || t('callCenterCommission.completeFailed'));
    } finally {
      setIsCompleting(false);
    }
  };

  const handleReverse = async () => {
    const ok = await confirm(
      t('callCenterCommission.reverseConfirmMessage').replace('{month}', commission?.settlementMonth || ''),
      { confirmText: t('callCenterCommission.reverse'), cancelText: t('common.cancel') }
    );
    if (!ok) return;

    setIsReversing(true);
    try {
      await reverseCommission(id);
      success(t('callCenterCommission.reverseSuccess'));
      navigate('/call-center/commission');
    } catch (err) {
      console.error('Error reversing commission:', err);
      showError(err.response?.data?.message || t('callCenterCommission.reverseFailed'));
    } finally {
      setIsReversing(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!commission || isDownloadingExcel) return;
    try {
      setIsDownloadingExcel(true);
      await exportCommissionDetailsExcel(commission, { featureLabel: 'call-center' });
    } catch (err) {
      console.error('Error exporting commission:', err);
      showError(t('callCenterCommission.failedToLoadCommission'));
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const columns = [
    { key: 'user', header: t('callCenterCommission.staffMember'), render: (row) => row.user?.name || '—' },
    {
      key: 'settlementMonth',
      header: t('callCenterCommission.settlementMonth'),
      width: '150px',
      render: () => commission?.settlementMonth || '—',
    },
    {
      key: 'createdAt',
      header: t('ecomOrders.createdAt'),
      width: '160px',
      render: (row) => (row.createdAt ? formatDateTime(row.createdAt) : '—'),
    },
    {
      key: 'commissionAmount',
      header: t('callCenterCommission.commissionAmount'),
      width: '160px',
      render: (row) => formatCurrency(row.commissionAmount),
    },
  ];

  return (
    <div className={styles.commissionDetailsPage}>
      <PageHeader
        title={commission ? `${t('callCenterCommission.detailsPageTitle')} — ${commission.settlementMonth}` : t('callCenterCommission.detailsPageTitle')}
        description={t('callCenterCommission.detailsPageDescription')}
        icon={Wallet}
        actions={
          <div className={styles.headerActions}>
            <button onClick={() => navigate('/call-center/commission')} className={styles.backButton}>
              <ArrowLeft size={18} />
              {t('common.back')}
            </button>
            {canReverse && commission?.status === 'pending' && (
              <button onClick={handleReverse} className={styles.dangerButton} disabled={isReversing}>
                <Undo2 size={20} />
                {isReversing ? t('common.processing') : t('callCenterCommission.reverse')}
              </button>
            )}
            {canFinalize && commission?.status === 'pending' && (
              <button onClick={handleMarkCompleted} className={styles.primaryButton} disabled={isCompleting}>
                <CheckCircle2 size={18} />
                {isCompleting ? t('common.processing') : t('callCenterCommission.markCompleted')}
              </button>
            )}
            <button onClick={handleDownloadExcel} className={styles.downloadExcelButton} disabled={isDownloadingExcel}>
              <Sheet size={16} />
              <span>{isDownloadingExcel ? t('common.processing') : t('callCenterCommission.excelButtonLabel')}</span>
            </button>
          </div>
        }
      />

      {commission && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>{t('callCenterCommission.totalCommission')}</span>
            <span className={styles.summaryValue}>{formatCurrency(commission.totalCommission)}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>{t('common.status')}</span>
            <span className={commission.status === 'completed' ? styles.completedBadge : styles.pendingBadge}>
              {t(`callCenterCommission.status.${commission.status}`)}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>{t('callCenterCommission.finalizedBy')}</span>
            <span className={styles.summaryValue}>{commission.finalizedBy?.name || '—'}</span>
          </div>
        </div>
      )}

      <DataTable
        data={commission?.staffCommissions || []}
        columns={columns}
        loading={isLoading}
        emptyMessage={t('callCenterCommission.noStaffCommissionRecords')}
      />
    </div>
  );
};

export default CommissionDetailsPage;
